import React, { useMemo, useRef, useState, useEffect } from 'react'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'

const defaultLoadingText = 'Finding the best offers for you'
const loadingPhaseTexts: [string, string, (room?: App.Occupants) => string] = [
  defaultLoadingText,
  'Checking your dates',
  (room) => {
    const parts = ['Checking room availability']
    if (room) {
      const additional: Array<string> = []
      if (room.adults > 0) {
        additional.push(`${pluralizeToString('adult', room.adults)}`)
      }
      if (room.childrenAge?.length) {
        additional.push(`${pluralizeToString('child', room.childrenAge.length)}`)
      }
      if (additional.length) {
        parts.push('for', additional.join(', '))
      }
    }
    return parts.join(' ')
  },
]

const MS_BETWEEN_LOADING_PHASES = 3500

interface Props {
  rooms?: Array<App.Occupants>
  onNewPhaseStarted?: () => void
}

function CalendarPickerRecommendationsLoadingIndicator(props: Props) {
  const { onNewPhaseStarted, rooms } = props
  const loadingTransitionScheduler = useRef<NodeJS.Timeout | null>(null)
  const [loadingPhase, setLoadingPhase] = useState<number>(0)

  const text = useMemo(() => {
    const loadingText = loadingPhaseTexts[loadingPhase]
    if (typeof loadingText === 'function') {
      return loadingText(rooms?.[0])
    }
    return loadingText ?? defaultLoadingText
  }, [loadingPhase, rooms])

  useEffect(() => {
    loadingTransitionScheduler.current = setTimeout(() => {
      if (loadingPhase < loadingPhaseTexts.length - 1) {
        setLoadingPhase(loadingPhase + 1)
        loadingTransitionScheduler.current = null
        if (onNewPhaseStarted) onNewPhaseStarted()
      }
    }, MS_BETWEEN_LOADING_PHASES)

    return () => {
      if (loadingTransitionScheduler.current) {
        clearTimeout(loadingTransitionScheduler.current)
      }
    }
  }, [loadingPhase, onNewPhaseStarted])

  return (
    <LoadingIndicator data-testid="loading-indicator" floating backdrop="grey-light">
      <BodyText align="center" colour="neutral-one" variant="large" weight="bold">{text}</BodyText>
    </LoadingIndicator>
  )
}

export default CalendarPickerRecommendationsLoadingIndicator
